import React, { useEffect, useMemo, useState, useContext } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSearch,
  FaTrash,
  FaChevronUp,
  FaChevronDown,
} from "react-icons/fa";

const BASE_URL = "http://nissi-india.com";

const ContractorProjectListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;
  const navigate = useNavigate();

  // -------------------- localStorage helpers --------------------
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined && val !== "" ? val : fallback;
  };

  const toStr = (val, fallback = "") => {
    if (val === null || val === undefined) return fallback;
    const str = String(val).trim();
    return str === "" ? fallback : str;
  };

  const toNum = (val, fallback = 0) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : fallback;
  };

  // -------------------- localStorage values --------------------
  const finYearId = useMemo(() => toStr(getLS("finYearId")), []);
  const finYear = useMemo(() => toStr(getLS("finYear")), []);
  const branchid = useMemo(
    () => toStr(getLS("branchId") || getLS("branchid")),
    []
  );
  const branchtype = useMemo(
    () => toStr(getLS("branchType") || getLS("branchtype"), "HO"),
    []
  );
  const loginuserid = useMemo(
    () =>
      toNum(
        getLS("userId") || getLS("loginuserid") || getLS("loginUserId"),
        0
      ),
    []
  );

  // -------------------- common headers --------------------
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchid,
      "x-branch": branchtype,
      "x-userid": loginuserid,
    }),
    [finYearId, finYear, branchid, branchtype, loginuserid]
  );

  const [showFilters, setShowFilters] = useState(true);

  // Search state
  const [searchData, setSearchData] = useState({
    contractorName: "",
    contractorId: "",
    projectName: "",
    mappingCode: "",
    contractorCode: "",
  });

  // dropdown states
  const [ddlContractorNames, setDdlContractorNames] = useState([]);
  const [ddlContractorCodes, setDdlContractorCodes] = useState([]);
  const [ddlProjects, setDdlProjects] = useState([]);
  const [ddlMappingCodes, setDdlMappingCodes] = useState([]);

  // table data
  const [projectData, setProjectData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ddlLoading, setDdlLoading] = useState(false);

  // checkbox state
  const [selectedIds, setSelectedIds] = useState([]);

  const allSelected = useMemo(() => {
    if (!projectData.length) return false;
    return selectedIds.length === projectData.length;
  }, [selectedIds, projectData]);

  // ✅ Total for Results Summary panel
  const totalAmount = useMemo(
    () =>
      (Array.isArray(projectData) ? projectData : []).reduce(
        (sum, p) => sum + (Number(p.amount) || 0),
        0
      ),
    [projectData]
  );

  // ------------------ Load Dropdowns ------------------
  const loadDropdowns = async () => {
    try {
      if (!branchid) {
        toast.error("BranchId not found in localStorage!");
        return;
      }

      setDdlLoading(true);

      const [names, codes, projects, maps] = await Promise.all([
        axios.get(`${BASE_URL}/api/contractorProject/contractors`, {
          params: { branchid },
          headers: commonHeaders,
        }),
        axios.get(`${BASE_URL}/api/contractorProject/contractorCodes`, {
          params: { branchid },
          headers: commonHeaders,
        }),
        axios.get(`${BASE_URL}/api/contractorProject/projects`, {
          params: { branchid, branchtype },
          headers: commonHeaders,
        }),
        axios.get(`${BASE_URL}/api/contractorProject/mappingCodes`, {
          params: { branchid },
          headers: commonHeaders,
        }),
      ]);

      setDdlContractorNames(Array.isArray(names.data?.data) ? names.data.data : []);
      setDdlContractorCodes(Array.isArray(codes.data?.data) ? codes.data.data : []);
      setDdlProjects(Array.isArray(projects.data?.data) ? projects.data.data : []);
      setDdlMappingCodes(Array.isArray(maps.data?.data) ? maps.data.data : []);
    } catch (err) {
      console.error("Dropdown Load Error:", err?.response?.data || err.message);
      toast.error(err?.response?.data?.message || "Failed to load dropdowns");
      setDdlContractorNames([]);
      setDdlContractorCodes([]);
      setDdlProjects([]);
      setDdlMappingCodes([]);
    } finally {
      setDdlLoading(false);
    }
  };

  useEffect(() => {
    loadDropdowns();
    handleSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (field, value) => {
    setSearchData((prev) => ({ ...prev, [field]: value }));
  };

  // ------------------ Search/List ------------------
  const handleSearch = async (silent = false, filters = searchData) => {
  try {
    if (!branchid) {
      toast.error("BranchId not found in localStorage!");
      return;
    }

    setLoading(true);
    setSelectedIds([]);

    const res = await axios.get(`${BASE_URL}/api/contractorProject/list`, {
      params: {
        branchid,
        branchtype,
        contractorId: filters.contractorId,
        contractorName: filters.contractorName,
        projectName: filters.projectName,
        mappingCode: filters.mappingCode,
        contractorCode: filters.contractorCode,
      },
      headers: commonHeaders,
    });

    setProjectData(Array.isArray(res.data?.data) ? res.data.data : []);

    if (!silent) toast.success("Search completed");
  } catch (err) {
    console.error("Search Error:", err?.response?.data || err.message);
    toast.error(err?.response?.data?.message || err.message);
    setProjectData([]);
  } finally {
    setLoading(false);
  }
};

  // ✅ Clear filters and reload full list
  const handleClearFilters = () => {
  const clearedFilters = {
    contractorName: "",
    contractorId: "",
    projectName: "",
    mappingCode: "",
    contractorCode: "",
  };

  setSearchData(clearedFilters);
  handleSearch(true, clearedFilters);
};

  // ------------------ Add New ------------------
  const handleAddNew = () => {
    navigate("/PaymentVouchers/ContractorProjectEntry");
  };

  // ------------------ Checkbox Logic ------------------
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(
        (Array.isArray(projectData) ? projectData : []).map((x) => x.mappingId)
      );
    }
  };

  const toggleRow = (mappingId) => {
    setSelectedIds((prev) => {
      if (prev.includes(mappingId)) {
        return prev.filter((id) => id !== mappingId);
      }
      return [...prev, mappingId];
    });
  };

  // ------------------ Delete (bulk, via Remove button) ------------------
  const deleteSelected = async () => {
    if (!selectedIds.length) {
      toast.warn("Please select at least one row to delete.");
      return;
    }

    if (!loginuserid) {
      toast.error("UserId not found in localStorage!");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} record(s)?`
    );
    if (!ok) return;

    try {
      await Promise.all(
        selectedIds.map((id) =>
          axios.delete(`${BASE_URL}/api/contractorProject/delete/${id}`, {
            params: { loginuserid, branchid },
            headers: commonHeaders,
          })
        )
      );

      toast.success("Deleted Selected Successfully");
      setSelectedIds([]);
      handleSearch(true);
    } catch (err) {
      console.error("Delete Selected Error:", err?.response?.data || err.message);
      toast.error(err?.response?.data?.message || err.message);
    }
  };

  // ------------------ Update — same navigation, now triggered per-row via Edit icon ------------------
  const handleUpdateRow = (row) => {
    navigate("/PaymentVouchers/ContractorProjectEntry", {
      state: { editData: row },
    });
  };

  const formatAmount = (amount) => {
    const n = Number(amount ?? 0);
    return n.toFixed(2);
  };

  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">CONTRACTOR PROJECT LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Contractor Project Listing
            </h1>

            <div className="flex items-center gap-3 flex-wrap">
              {ddlLoading && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading dropdowns...
                </span>
              )}

              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleClearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={() => handleSearch(false)}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch /> Search
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Contractor Name
                  </label>
                  <select
                    value={searchData.contractorName}
                    onChange={(e) => handleSearchChange("contractorName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {ddlContractorNames.map((x, index) => (
                      <option key={`${x.value || index}-${x.label || index}`} value={x.label}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Contractor Code
                  </label>
                  <select
                    value={searchData.contractorCode}
                    onChange={(e) => handleSearchChange("contractorCode", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {ddlContractorCodes.map((x, index) => (
                      <option key={`${x.value || index}-${x.label || index}`} value={x.label}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Project Name
                  </label>
                  <select
                    value={searchData.projectName}
                    onChange={(e) => handleSearchChange("projectName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {ddlProjects.map((x, index) => (
                      <option key={`${x.value || index}-${x.label || index}`} value={x.label}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Mapping Code
                  </label>
                  <select
                    value={searchData.mappingCode}
                    onChange={(e) => handleSearchChange("mappingCode", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {ddlMappingCodes.map((x, index) => (
                      <option key={`${x.value || index}-${x.label || index}`} value={x.label}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            <table className={`w-full text-sm ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-semibold">Sl.No</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">Mapping Code</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">Contractor Id</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">Contractor Code</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">Contractor Name</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">Work Type Name</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">Project Name</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">Amount</th>

                  {/* EDIT - SECOND LAST COLUMN */}
                  <th className="px-4 py-4 text-center text-sm font-semibold">Edit</th>

                  {/* SELECT ALL - LAST COLUMN */}
                  <th className="px-4 py-4 text-center text-sm font-semibold w-16">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 cursor-pointer"
                      title="Select All"
                    />
                  </th>
                </tr>
              </thead>

              <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                {loading ? (
                  <tr>
                    <td colSpan={10} className={`px-4 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Loading...
                    </td>
                  </tr>
                ) : projectData.length === 0 ? (
                  <tr>
                    <td colSpan={10} className={`px-4 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      No records found
                    </td>
                  </tr>
                ) : (
                  projectData.map((p, index) => {
                    const checked = selectedIds.includes(p.mappingId);

                    return (
                      <tr
                        key={p.mappingId || index}
                        className={`${
                          index % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {p.slNo}
                        </td>
                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {p.mappingCode}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {p.contractorId}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {p.contractorCode}
                        </td>
                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                          {p.contractorName}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {p.workTypeName}
                        </td>
                        <td className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {p.projectName}
                        </td>
                        <td className={`px-4 py-3 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>
                          {formatAmount(p.amount)}
                        </td>

                        {/* EDIT */}
                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleUpdateRow(p)}
                            className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                            title="Edit"
                          >
                            ✏️
                          </button>
                        </td>

                        {/* SELECT */}
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRow(p.mappingId)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* ==================== REMOVE BUTTON ==================== */}
            <div className="p-4">
              <button
                onClick={deleteSelected}
                disabled={selectedIds.length === 0}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                  selectedIds.length === 0 ? "opacity-50 cursor-not-allowed" : ""
                } bg-red-600 hover:bg-red-700 text-white`}
              >
                🗑
                Remove
              </button>
            </div>
          </div>
          {/* ==================== TOAST ==================== */}
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default ContractorProjectListing;